import React, { useState } from "react";
import { useGetCollaborativeGroupWork } from "@packages/api/madlenAPI";
import { Expand, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import Text from "@core/text";
import { Button } from "@core/index";
import { CollaborativeGroupWorkResponseDto } from "@packages/api/madlenAPI.schemas";
import CollaborativeCard from "@features/tools/components/collaborative/CollaborativeCard";
import Markdown from "react-markdown";
import SpinnerLoader from "../components/SpinnerLoader";

interface CollaborativeGroupWorkProps {
  id: number | null;
}

const CollaborativeGroupWorkDetailComponent = ({
  tool,
  closeModal,
}: {
  tool: CollaborativeGroupWorkResponseDto;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 dark:bg-opacity-90 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <Text variant={"h1"}>{tool.title || t("collaborative.title")}</Text>
          <Button
            onClick={closeModal}
            variant="solid"
            size="icon"
            rounded="full"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={24} className="text-gray-500 dark:text-gray-400" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-6 modal-content">
          <CollaborativeCard collaborative={tool} />
        </div>
      </div>
    </div>
  );
};

const CollaborativeGroupWork: React.FC<CollaborativeGroupWorkProps> = ({
  id,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError } = useGetCollaborativeGroupWork(id, {
    query: {
      refetchInterval: (data) => {
        if (
          data?.state.data?.reflectionQuestions &&
          data?.state.data?.reflectionQuestions.length > 0
        ) {
          return false;
        }
        return 2000;
      },
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30,
    },
  });

  const isContentLoading =
    isLoading ||
    !data?.reflectionQuestions ||
    data.reflectionQuestions.length === 0;

  const handleExpandClick = () => {
    setExpanded(true);
  };

  const closeModal = () => {
    setExpanded(false);
  };

  if (isContentLoading) {
    return <SpinnerLoader />;
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <h3 className="text-xl font-medium mb-4 text-red-500 dark:text-red-400">
          {t("collaborative.error.unableToLoad")}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("collaborative.error.tryRegenerate")}
        </p>
      </div>
    );
  }

  return (
    <>
      {expanded && data && (
        <CollaborativeGroupWorkDetailComponent
          tool={{
            ...data,
            gradeLevel: data.gradeLevel === -1 ? 0 : data.gradeLevel,
          }}
          closeModal={closeModal}
        />
      )}

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex w-full justify-between items-center">
          <h3 className="text-xl font-medium mb-4 flex items-center justify-between text-gray-900 dark:text-gray-100">
            <div>{t("collaborative.title")}</div>
          </h3>
          <button
            className="flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
            onClick={handleExpandClick}
          >
            <Expand size={18} className="text-gray-400 dark:text-gray-500" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("collaborative.subtitle")}
        </p>

        {data && data.groupRoles && data.groupRoles.length > 0 && (
          <div className="relative mt-4">
            <div className="space-y-6">
              {data.groupRoles.slice(0, 1).map((role, idx) => (
                <div key={idx}>
                  {role.title && (
                    <h5 className="font-medium text-lg mb-6 text-[#8D6B4E] dark:text-[#C8B08D] pb-3 border-b border-[#BFA58A]/20 dark:border-[#BFA58A]/10">
                      {role.title}
                    </h5>
                  )}
                  <div className="space-y-6">
                    {role.responsibilities && (
                      <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/20 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5E0C3]/30 dark:bg-[#6D4C3D]/30 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
                          <span className="font-medium text-[#8D6B4E] dark:text-[#C8B08D]">
                            {t("collaborative.responsibilities")}
                          </span>
                        </div>
                        <Markdown className="mt-3 text-gray-700 dark:text-gray-300">
                          {role.responsibilities}
                        </Markdown>
                      </div>
                    )}
                    {role.skills_developed && (
                      <div className="bg-[#F5E0C3]/10 dark:bg-[#6D4C3D]/20 rounded-lg p-4 shadow-inner hover:shadow-md transition-all duration-300">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5E0C3]/30 dark:bg-[#6D4C3D]/30 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
                          <span className="font-medium text-[#8D6B4E] dark:text-[#C8B08D]">
                            {t("collaborative.skillsDeveloped")}
                          </span>
                        </div>

                        <Markdown className="mt-3 text-gray-700 dark:text-gray-300">
                          {role.skills_developed}
                        </Markdown>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CollaborativeGroupWork;
